import { css } from '@emotion/core'
import { EXPERIENCE_NAV_PILL_BLOCK_SIZE_CSS } from './experienceNavPosition.styles'
import { GLOBAL_HEADER_HEIGHT_CSS } from '../../hooks/useAvailableViewportHeightCss'

// One surface, shared by everything the navigation controls float over the
// experience: the card, the handle that opens it, and the stepper that walks
// its list. Literal values: these float over experience artwork (the card is even portalled to the
// document body), so they can't count on a zion theme surface above them to
// resolve color tokens. The values are the day-theme gray00 / gray100 /
// danger50.
export const NAV_SURFACE_BACKGROUND = '#ffffff'
export const NAV_SURFACE_COLOR = '#202121'
// Card rows composite this over the surface; the floating controls have
// artwork behind them rather than the surface, so they need the same value
// already flattened.
export const NAV_SURFACE_HOVER_OVERLAY = 'rgba(0, 0, 0, 0.06)'
export const NAV_SURFACE_HOVER_BACKGROUND = '#f0f0f0'
// Nothing dims the artwork behind a closed control the way the backdrop dims
// it behind the open card, so they carry their own separation.
export const NAV_SURFACE_SHADOW = '0 2px 12px rgba(0, 0, 0, 0.3)'
export const LEAVE_COLOR = '#d94233'

// The floating controls must paint above any full-screen surface an
// experience renders.
export const NAV_SURFACE_Z_INDEX = 2147483647

// Where the handle sits: flush against the side (STATION, thumb-reachable at
// any height) or in the top-start corner (WEB).
export const EDGE_TAB_PLACEMENT = 'edge-tab'
export const TOP_CORNER_PLACEMENT = 'top-corner'

// STATION: a tab flush against the physical left edge, half-round on the open
// side. The horizontal radius exceeds half the height, so the bulge is a
// flattened ellipse rather than a strict semicircle.
export const NAV_TRIGGER_INLINE_SIZE = '48px'
export const NAV_TRIGGER_BLOCK_SIZE = '80px'

export const SAFE_AREA_TOP_CSS = 'env(safe-area-inset-top, 0px)'
export const SAFE_AREA_LEFT_CSS = 'env(safe-area-inset-left, 0px)'
export const SAFE_AREA_RIGHT_CSS = 'env(safe-area-inset-right, 0px)'
// Where the controls sit, as INPUT custom properties an experience may
// override (the mirror of the --experience-nav-*-clearance properties, which
// only useExperienceNavCssVars writes). Both default to 24px. Block-start is
// measured from the top of the content area - below the global header when
// it's visible, the viewport top otherwise - so an experience aligning the
// controls with its own top band (heritage's country bar, the tab bars) sets
// the same values its band uses. Set them on :root (or any ancestor) and
// scope them to the experience's lifetime.
export const NAV_BLOCK_START_CSS = 'var(--experience-nav-block-start, 24px)'
export const NAV_INLINE_START_CSS = 'var(--experience-nav-inline-start, 24px)'
// The stepper's offset from the END edge. Follows inline-start unless an
// experience owns that corner (the temple map's control rail lives there) and
// pushes the stepper inward on its own.
export const NAV_INLINE_END_CSS = 'var(--experience-nav-inline-end, var(--experience-nav-inline-start, 24px))'

// The top-corner placement (WEB), dropped below the global header whenever
// that header is actually on screen. Shared so the menu button and the
// stepper on the opposite edge sit on exactly the same line.
export const CORNER_NAV_SIZE_CSS = EXPERIENCE_NAV_PILL_BLOCK_SIZE_CSS
export const CORNER_NAV_TOP_CSS = `calc(${SAFE_AREA_TOP_CSS} + ${NAV_BLOCK_START_CSS})`
export const CORNER_NAV_HEADER_TOP_CSS = `calc(${GLOBAL_HEADER_HEIGHT_CSS} + ${NAV_BLOCK_START_CSS})`
export const CORNER_NAV_LEFT_CSS = `calc(${SAFE_AREA_LEFT_CSS} + ${NAV_INLINE_START_CSS})`
export const CORNER_NAV_RIGHT_CSS = `calc(${SAFE_AREA_RIGHT_CSS} + ${NAV_INLINE_END_CSS})`

// Room left between a floating control and whatever sits beside or below it -
// the same distance the controls keep from the edge, so a neighbour is as far
// from a control as the control is from the edge.
export const NAV_BREATHING_ROOM_PX = 24

// Vertical space the top-start controls occupy, measured from the top edge of
// the content area: the notch/status-bar inset (nonzero only in edge-to-edge
// contexts like the Together TWA), the block-start offset above them, their
// height, and the breathing room below. The WEB corner trigger lands on these
// offsets at this size (in the Together WebView too). Published as
// --experience-nav-clearance (see
// useExperienceNavCssVars) and exported for surfaces that know their own
// controls are present and want the formula directly.
export const NAV_CLEARANCE_CSS = `calc(${SAFE_AREA_TOP_CSS} + ${NAV_BLOCK_START_CSS} + ${CORNER_NAV_SIZE_CSS} + ${NAV_BREATHING_ROOM_PX}px)`

// The tighter gap a bar leaves after the inline-start controls (a tab bar's
// gutter beside the edge tab): those are flush edges meeting, not two
// floating controls sharing a corner.
export const NAV_GAP_PX = 12

// One arrow of the stepper; the pair is twice this, joined.
export const NAV_STEP_BUTTON_INLINE_SIZE_PX = 38

// Inline room the stepper takes on the top-END corner, measured from the end
// edge: its offset from the edge, both arrows, and breathing room after them. The
// stepper is the only control that ever sits there, and only in WEB (STATION
// centers it against the side, mirroring the edge tab). Published as
// --experience-nav-inline-end-clearance (see useExperienceNavCssVars) for
// content that owns that corner - heritage's category pane puts its close
// button there - and 0px whenever nothing does.
export const NAV_INLINE_END_CLEARANCE_CSS = `calc(${CORNER_NAV_RIGHT_CSS} + ${2 * NAV_STEP_BUTTON_INLINE_SIZE_PX}px + ${NAV_BREATHING_ROOM_PX}px)`

// How a row's glyph sits in whatever holds it: a card row, or the handle
// itself when the card would hold only that row. Experiences hand us their
// category artwork as <img>, and it was drawn white for the retired
// translucent-black pills - invisible on the white surface. brightness(0) flattens any
// glyph to solid dark while keeping its alpha, so white line art reads
// correctly without each experience shipping a second, light-surface icon
// set. Emoji arrive wrapped in an aria-hidden span and get a fixed box so
// they line up with the drawn icons.
export const rowGlyphCss = css`
  & svg,
  & img {
    width: 24px;
    height: 24px;
    object-fit: contain;
    flex-shrink: 0;
  }

  & img {
    filter: brightness(0);
  }

  & > span[aria-hidden='true'] {
    flex-shrink: 0;
    width: 24px;
    font-size: 22px;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`

// The keyboard focus ring every control on the surface draws: inside its own
// box, in its own colour, so it reads on the white surface and never clips.
export const focusRingCss = css`
  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: -2px;
  }
`

// Leave Kiosk alone is coloured as an exit, in the card and on the handle.
export const leaveRowCss = css`
  color: ${LEAVE_COLOR};
`
